import React, { memo } from "react";
import { Divider } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { Badge } from "../preview/Badge";
import { EscortFeeSummary } from "./EscortFeeSummary";
import { ParticipantFullSummary } from "../../types/participant.types";
import { RegistrationRequest } from "../../types/registration.types";
import { ConferenceConfiguration } from "../../types/conference.types";
import {
  Fee,
  FeeType,
  ParticipantFeeSummary,
} from "../../types/fee.types";
import { NO_VALUE } from "../../utils/constants";
import type { ParticipantSummaryChanges } from "./participantSummarySectionTypes";

export interface ParticipantSummaryEscortFeeSectionProps {
  participant: ParticipantFullSummary;
  modifiedData: RegistrationRequest | null | undefined;
  displayConfigJson: ConferenceConfiguration;
  changes: ParticipantSummaryChanges;
  allParticipantFees: Fee[];
}

const emptyEscortFeeSummary = {
  name: NO_VALUE,
  feeChoiceId: 0,
  participantId: 0,
  organizingPrice: 0,
  lunchPrice: 0,
  totalPrice: 0,
  feeType: FeeType.ESCORT,
  numberOfAttendants: 0,
  attendantsName: "",
  participantFeeId: 0,
} satisfies ParticipantFeeSummary;

const ParticipantSummaryEscortFeeSectionInner: React.FC<
  ParticipantSummaryEscortFeeSectionProps
> = ({
  participant,
  modifiedData,
  displayConfigJson,
  changes,
  allParticipantFees,
}) => {
  const hasModifiedEscortFeeData = !!(
    (modifiedData?.escortFee?.numberOfAttendants ?? 0) > 0 ||
    modifiedData?.escortFee?.attendantsName?.trim()
  );
  const showEscortFeeSection =
    participant.escortFee ||
    (!!modifiedData &&
      displayConfigJson.participantFeeEnabled &&
      hasModifiedEscortFeeData);

  if (!showEscortFeeSection) return null;

  let feeBody: React.ReactNode = null;
  if (
    changes["escortFee"] !== undefined &&
    changes["escortFee"].modified &&
    modifiedData?.escortFee
  ) {
    const modifiedFee = allParticipantFees.find(
      (f) => f.id === modifiedData.escortFee?.participationFeeId,
    );
    if (modifiedFee) {
      const modifiedEscortFeeSummary: ParticipantFeeSummary = {
        feeChoiceId: participant.escortFee?.feeChoiceId || 0,
        participantId: participant.id,
        name: modifiedFee.name,
        organizingPrice: modifiedFee.price,
        lunchPrice: modifiedFee.lunchPrice,
        totalPrice: modifiedFee.totalPrice,
        feeType: modifiedFee.feeType,
        numberOfAttendants: modifiedData.escortFee.numberOfAttendants || 0,
        attendantsName: modifiedData.escortFee.attendantsName || "",
        participantFeeId: participant.escortFee?.participantFeeId || 0,
      };
      feeBody = (
        <EscortFeeSummary
          escortFee={modifiedEscortFeeSummary}
          originalEscortFee={
            participant.escortFee ?? emptyEscortFeeSummary
          }
        />
      );
    }
  } else if (participant.escortFee) {
    feeBody = <EscortFeeSummary escortFee={participant.escortFee} />;
  }

  return (
    <>
      <Badge color="gray">
        <Trans>Escort Fee</Trans>
      </Badge>
      {feeBody}
      <Divider />
    </>
  );
};

export const ParticipantSummaryEscortFeeSection = memo(
  ParticipantSummaryEscortFeeSectionInner,
);
